import { z } from 'zod';
import {
  PAGINATION_PAGE_SIZE_LARGE,
  PAGINATION_SORT_DIRECTION,
} from '~/constants';

import { toSnakeCase } from '@vestigas/utils';

import {
  ACCOUNTING_REFERENCE_SIGNATURE_RATES_BREAKDOWN_SORT_BY,
  ACCOUNTING_REFERENCE_SIGNATURE_RATES_SIGNATURE_FILTER,
} from './constants';

/**
 * Zod schema for validating top accounting references breakdown query parameters.
 */
export const zodTopAccountingReferencesBreakdownQueryParamsSchema = z.object({
  dateRange: z.object({
    dateFrom: z.iso.date(),
    dateTo: z.iso.date(),
  }),
  limit: z.number().int().positive().prefault(PAGINATION_PAGE_SIZE_LARGE),
  offset: z.number().int().nonnegative().prefault(0),
  signatureFilter: z
    .preprocess(
      (value) => (typeof value === 'string' ? toSnakeCase(value) : value),
      z.enum(ACCOUNTING_REFERENCE_SIGNATURE_RATES_SIGNATURE_FILTER),
    )
    .prefault('all'),
  sortBy: z
    .preprocess(
      (value) => (typeof value === 'string' ? toSnakeCase(value) : value),
      z.enum(ACCOUNTING_REFERENCE_SIGNATURE_RATES_BREAKDOWN_SORT_BY),
    )
    .optional()
    .prefault('signature_rate'),
  sortOrder: z
    .preprocess(
      (value) => String(value).toUpperCase(),
      z.enum(PAGINATION_SORT_DIRECTION),
    )
    .prefault('DESC'),
});

/**
 * Zod schema for validating a single accounting reference breakdown item.
 */
const zodAccountingReferenceBreakdownItemSchema = z.object({
  accrefId: z.uuid(),
  accrefName: z.string(),
  signatureRate: z.number().nonnegative(),
  signedDeliveryNotesCount: z.number().int().nonnegative(),
  totalDeliveryNotesCount: z.number().int().nonnegative(),
  unsignedDeliveryNotesCount: z.number().int().nonnegative(),
});

/**
 * Zod schema for validating the top accounting references breakdown API response.
 */
export const zodTopAccountingReferencesBreakdownResponseSchema = z.object({
  data: z.array(zodAccountingReferenceBreakdownItemSchema),
  hasNextPage: z.boolean(),
  totalCount: z.number().int().nonnegative(),
});

/**
 * Zod schema for validating the top accounting references breakdown API endpoint path segments.
 */
export const zodTopAccountingReferencesBreakdownEndpointSchema = z.tuple([
  z.literal('accounting_reference'),
  z.literal('statistics'),
  z.literal('top_accounting_references'),
]);
